import { ErrorBoundary } from "@sentry/react";
import { Card } from "antd";
import FeatureToFeatureLinksManagePanel from "components/feature-to-feature-links-manage-panel";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import {
  TFeatureWorkflowInstance,
  TFetureDetailUpdateAction
} from "entity-app/models";
import EntityDateblock from "entity-app/shared-components/entity-dateblock";
import { TGoverningSource } from "entity-app/shared-components/entity-dateblock/models";
import { getTypeOfGoverningSourceTitle } from "entity-app/utils/utils";
import ProjectWorkflowTemplateDropdown from "pages/preconstruction-details/components/workflow-template-dropdown";
import { useContext, useMemo, useState } from "react";

function HeadingDateBlock(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  disable: boolean;
  setTempDisable: (isLoading: boolean) => void;
}) {
  const { featureInstanceData, actions, disable, setTempDisable } = props;
  if (!featureInstanceData) return null;
  const { feature_workflow_instance: featureWorkflowInstance } =
    featureInstanceData as {
      feature_workflow_instance: TFeatureWorkflowInstance;
    };

  return (
    <div className="flex w-full items-center space-x-4">
      <div className="flex items-center">Design Package Schedule</div>

      <div className="flex items-center space-x-1 font-medium">
        <div className="text-xs font-medium">Template:</div>
        <ProjectWorkflowTemplateDropdown
          afterChange={() => {
            actions.refetchFeatureInstanceData();
          }}
          featureId={FeatureTypes.DESIGN_PACKAGE}
          featureWorkflowInstance={featureWorkflowInstance}
          disable={disable}
          setApiCallLoading={setTempDisable}
        />
      </div>
    </div>
  );
}

function DesignLinkedTopSection(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  disabled: boolean;
}) {
  const { featureInstanceData, actions, disabled } = props;
  const [tempDisable, setTempDisable] = useState(false);
  const {
    columnHeaders: { designPackageHeaderMap }
  } = useContext(ProjectContext);

  const governingSource: TGoverningSource | undefined = useMemo(() => {
    const governingTask = (
      featureInstanceData?.links_as_source as Array<any>
    )?.find((x) => x.governing_link);

    if (governingTask) {
      return {
        id: governingTask.target_feature_instance.feature_workflow_instance
          .feature_instance_id,
        name: governingTask.target_feature_instance.title,
        type: getTypeOfGoverningSourceTitle(
          governingTask.target_feature_instance.feature_type_id
        ),
        isLinkedEndDate: governingTask.link_type === 2
      };
    }
    if (featureInstanceData?.feature_workflow_instance?.manual_entry_date) {
      return {
        id: featureInstanceData.feature_workflow_instance.feature_instance_id,
        name: "Manually Set Date",
        type: "Manually Set Date"
      };
    }
    return undefined;
  }, [featureInstanceData]);

  return (
    <Card
      title={
        <HeadingDateBlock
          featureInstanceData={featureInstanceData}
          actions={actions}
          disable={tempDisable}
          setTempDisable={setTempDisable}
        />
      }
      bodyStyle={{ padding: 0 }}
      className="ciq-card-block"
    >
      <div className="w-full h-[470px] flex">
        <div className="grow h-full">
          <div className="overflow-auto px-3 flex h-full">
            <ErrorBoundary>
              <EntityDateblock
                entity={featureInstanceData}
                afterChange={() => {
                  actions.refetchFeatureInstanceData();
                }}
                governingSource={governingSource}
                permissions={
                  tempDisable
                    ? {}
                    : {
                        canEditActualDate: !disabled,
                        canEditProjectedDate: !disabled,
                        canEditDurations: !disabled,
                        canEditPlannedDate: !disabled
                      }
                }
                setApiCallLoading={setTempDisable}
                entityHeaders={designPackageHeaderMap}
              />
            </ErrorBoundary>
          </div>
        </div>
        <div className="w-[280px] shrink-0 overflow-y-auto border-0 border-l border-solid border-ciq-gray-300">
          <ErrorBoundary>
            <FeatureToFeatureLinksManagePanel
              featureId={featureInstanceData?.id}
              title="Linked Bid Packages"
              showDependencyButton
              disabled={disabled}
            />
          </ErrorBoundary>
        </div>
      </div>
    </Card>
  );
}
export default DesignLinkedTopSection;
